'use client';

import { Box } from '@mui/material';

import { ArticleShareLinks } from '@/components/ResourceCenter/ArticleShareLinks';
import { ArticleSidebarCtaCard } from '@/components/ResourceCenter/ArticleSidebarCtaCard';
import { AuthorCardSidebar } from '@/components/Sidebar/AuthorCardSidebar';
import { FunnelLeadCaptureSidebar } from '@/components/Sidebar/FunnelSidebar';
import { JumpLinksSidebar } from '@/components/Sidebar/JumpLinksSidebar';
import { MetricSidebar } from '@/components/Sidebar/MetricSidebar';
import type { JumpLink } from '@/lib/helpers/contentHelpers';
import {
  ComponentBlocksAuthorDetails,
  ComponentBlocksFunnel,
  ComponentBlocksMetricHighlight,
  ComponentSharedArticleSidebarCta,
  ResourceArticle,
} from '@/types';

interface ArticleSidebarProps {
  article: ResourceArticle;
  jumpLinks: JumpLink[];
  showJumpLinks: boolean;
}

export function ArticleSidebar({ article, jumpLinks, showJumpLinks }: ArticleSidebarProps) {
  const articleSidebarCta = article.Sidebar?.ArticleSidebarCta as
    | ComponentSharedArticleSidebarCta
    | undefined;
  const leadFunnel = article.Sidebar?.LeadFunnel as ComponentBlocksFunnel | undefined;
  const authorCard = article.Sidebar?.AuthorCard as ComponentBlocksAuthorDetails | undefined;
  const metricHighlights = (article.Sidebar?.MetricHighlight?.filter(Boolean) ??
    []) as ComponentBlocksMetricHighlight[];
  const author = authorCard?.WrittenBy ?? article.Author;
  const articlePath =
    article.ResourceCategory?.Slug && article.Slug
      ? `/resources/${article.ResourceCategory.Slug}/${article.Slug}`
      : '';
  const hasSidebar = Boolean(
    articleSidebarCta ||
      leadFunnel ||
      author ||
      metricHighlights.length ||
      (showJumpLinks && jumpLinks.length)
  );

  if (!hasSidebar) return null;

  return (
    <Box
      data-component="ArticleSidebar"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        position: { lg: 'sticky' },
        top: { lg: 120 },
        alignSelf: 'start',
      }}
    >
      {articleSidebarCta && (
        <ArticleSidebarCtaCard
          shareTitle={article.ResourceTitle}
          sharePath={articlePath}
          contentLabel="article"
          cta={articleSidebarCta}
        />
      )}

      {!articleSidebarCta && leadFunnel && (
        <FunnelLeadCaptureSidebar block={leadFunnel}>
          <ArticleShareLinks
            title={article.ResourceTitle}
            path={articlePath}
            contentLabel="article"
          />
        </FunnelLeadCaptureSidebar>
      )}

      {author && (
        <AuthorCardSidebar
          WrittenBy={author}
          ShowBio={authorCard?.ShowBio}
          ShowSocialLabels={authorCard?.ShowSocialLabels}
          id={authorCard?.id ?? ''}
        />
      )}

      {metricHighlights.map((metricHighlight) => (
        <MetricSidebar key={metricHighlight.id} block={metricHighlight} />
      ))}

      {showJumpLinks && jumpLinks.length > 0 && <JumpLinksSidebar links={jumpLinks} />}
    </Box>
  );
}
